import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IGetCategoriesItemDto } from 'app/main/pages/categories.dto';
import { IDocumentDto } from 'app/main/pages/documents/dto/document-listing.dto';
import { Field } from 'app/interfaces/fields';
import { ITableCellItem } from '../../table-cell';
import { ITableColumn } from '../../table-column';
import { ITableDataItem } from '../../table-data-item';

@Component({
    selector: 'app-category-value',
    templateUrl: './category-value.component.html',
    styleUrl: './category-value.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CategoryValueComponent implements ITableCellItem {
    private _categoryValue = '';
    private _dataItem: ITableDataItem;
    private _columnOptions: ITableColumn;

    @Input() set dataItem(dataItem: ITableDataItem) {
        this._dataItem = dataItem;
        this.updateCategoryValue();
    }

    @Input() set columnOptions(columnOptions: ITableColumn) {
        this._columnOptions = columnOptions;
        this.updateCategoryValue();
    }

    get categoryValue(): string {
        return this._categoryValue;
    }

    private updateCategoryValue(): void {
        this._categoryValue = this.calculateCategoryValue();
    }

    private calculateCategoryValue(): string {
        if (!this._dataItem || !this._columnOptions) {
            return '';
        }

        const fieldName = this._columnOptions.field;
        if (!fieldName) {
            return '';
        }

        const documentData = this._dataItem as IDocumentDto & {
            category_values?: Record<string, string>;
            categories?: Array<{ id?: number; name?: string; category_name?: string; parent_id?: number; parent_category?: number }>;
        };
        const field = this.findFieldByName(documentData, fieldName);

        if (field) {
            const altValue = typeof field.alt_value === 'string' ? field.alt_value.trim() : '';
            if (altValue) {
                return altValue;
            }

            const selectedIds = this.normalizeToIds(field.value ?? field.selected_option_id);
            if (selectedIds.length) {
                const categoryNames = this.resolveCategoryNames(selectedIds, field.category);
                if (categoryNames.length) {
                    return categoryNames.join(', ');
                }
                return selectedIds.join(', ');
            }

            if (Array.isArray(field.value)) {
                return field.value.map(value => String(value)).join(', ');
            }

            if (field.value != null) {
                return String(field.value);
            }
        }

        const documentCategories = documentData.categories as Array<{ id?: number; name?: string; category_name?: string; parent_id?: number; parent_category?: number }> | undefined;
        if (documentCategories && Array.isArray(documentCategories)) {
            const categoryIdMatch = fieldName.match(/category_(\d+)/);
            if (!categoryIdMatch) {
                return '';
            }

            const categoryId = Number(categoryIdMatch[1]);
            const selectedCategory = documentCategories.find(cat => cat.id === categoryId);

            if (selectedCategory) {
                if (!selectedCategory.parent_id && !selectedCategory.parent_category) {
                    const deepestDescendants = this.getDeepestDescendants(selectedCategory.id, documentCategories);
                    if (deepestDescendants.length > 0) {
                        return deepestDescendants.join(', ');
                    }
                }
                
                return selectedCategory.category_name || selectedCategory.name || '';
            }
        }

        if (documentData.category_values && typeof documentData.category_values === 'object') {
            const values = Object.values(documentData.category_values)
                .filter((value: any) => value && typeof value === 'string' && value.trim() !== '');

            if (values.length > 0) {
                return values.join(', ');
            }
        }

        const directValue = documentData[fieldName];
        if (directValue) return directValue;

        return '';
    }

    private getDeepestDescendants(parentId: number, categories: any[]): string[] {
        const descendants: string[] = [];

        const directChildren = categories.filter(cat => cat.parent_id === parentId || cat.parent_category === parentId);

        for (const child of directChildren) {
            const hasChildren = categories.some(cat => cat.parent_id === child.id || cat.parent_category === child.id);

            if (hasChildren) {
                const deeperDescendants = this.getDeepestDescendants(child.id, categories);
                descendants.push(...deeperDescendants);
            } else {
                const childName = child.category_name || child.name || '';
                if (childName.trim() !== '') {
                    descendants.push(childName);
                }
            }
        }
        return descendants;
    }

    private findFieldByName(documentData: IDocumentDto, fieldName: string): (Field & { category?: IGetCategoriesItemDto | null }) | null {
        const fields = documentData?.fields_datas?.data;
        if (!fields) {
            return null;
        }

        return Object.values(fields).find((field) => field?.field_name === fieldName) as (Field & { category?: IGetCategoriesItemDto | null }) | undefined ?? null;
    }

    private normalizeToIds(value: unknown): number[] {
        if (Array.isArray(value)) {
            return value
                .map((item) => Number(item))
                .filter((item) => Number.isFinite(item));
        }

        const normalized = Number(value);
        return Number.isFinite(normalized) ? [normalized] : [];
    }

    private resolveCategoryNames(selectedIds: number[], rootCategory?: IGetCategoriesItemDto | null): string[] {
        if (!rootCategory) {
            return [];
        }

        const availableCategories = this.flattenCategories(rootCategory);
        return selectedIds
            .map((id) => availableCategories.find((category) => category.id === id)?.category_name)
            .filter((name): name is string => !!name?.trim());
    }

    private flattenCategories(category: IGetCategoriesItemDto): IGetCategoriesItemDto[] {
        const flattened: IGetCategoriesItemDto[] = [category];
        const children = category.sub_category || category.children || [];

        for (const child of children) {
            flattened.push(...this.flattenCategories(child));
        }

        return flattened;
    }

}
